import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useNotifications } from "@/contexts/NotificationContext";
import { Bell, CheckCheck } from "lucide-react";
import SegmentedToggle from "@/components/SegmentedToggle";

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("newest");
  const [filter, setFilter] = useState("all"); // all | unread
  const navigate = useNavigate();
  const notifCtx = useNotifications();

  const load = () => {
    setLoading(true);
    api.get("/notifications", { params: { sort } })
      .then(r => setItems(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(load, [sort]);

  const markRead = async (n) => {
    if (!n.read) {
      await api.post(`/notifications/${n.id}/read`);
      setItems(items.map(i => i.id === n.id ? { ...i, read: true } : i));
      notifCtx?.refreshUnread();
    }
    if (n.url) navigate(n.url);
  };

  const markAllRead = async () => {
    await api.post("/notifications/mark-all-read");
    setItems(items.map(i => ({ ...i, read: true })));
    notifCtx?.refreshUnread();
  };

  const visible = filter === "unread" ? items.filter(i => !i.read) : items;
  const unreadCount = items.filter(i => !i.read).length;

  return (
    <div className="max-w-3xl mx-auto px-4 py-12" data-testid="notifications-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Bell className="w-6 h-6" /> Notifications
        </h1>
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={markAllRead}
            className="text-sm text-slate-600 hover:text-black flex items-center gap-1.5"
            data-testid="mark-all-read"
          >
            <CheckCheck className="w-4 h-4" /> Mark all as read
          </button>
        )}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3 mt-5">
        <SegmentedToggle
          options={[{ value: "all", label: "All" }, { value: "unread", label: `Unread${unreadCount ? ` (${unreadCount})` : ""}` }]}
          value={filter}
          onChange={setFilter}
        />
        <SegmentedToggle
          options={[{ value: "newest", label: "Newest first" }, { value: "oldest", label: "Oldest first" }]}
          value={sort}
          onChange={setSort}
        />
      </div>

      <div className="mt-6 space-y-3">
        {loading ? (
          [...Array(4)].map((_, i) => <div key={i} className="skeleton h-20 rounded-xl" />)
        ) : visible.length === 0 ? (
          <div className="text-slate-500 text-sm text-center py-16 border border-dashed border-slate-200 rounded-xl">
            {filter === "unread" ? "You're all caught up." : "No notifications yet."}
          </div>
        ) : (
          visible.map(n => (
            <button
              key={n.id}
              onClick={() => markRead(n)}
              data-testid={`notification-${n.id}`}
              className={`w-full text-left p-4 rounded-xl border transition-colors ${
                n.read ? "bg-white border-slate-200 hover:border-slate-300" : "bg-[#E8F0FE] border-[#1D6FF2]/30"
              }`}
            >
              <div className="flex items-start gap-2">
                {!n.read && <span className="w-2 h-2 rounded-full bg-[#1D6FF2] mt-1.5 shrink-0" />}
                <div className={n.read ? "pl-4" : ""}>
                  <div className="font-medium text-slate-900">{n.title}</div>
                  <div className="text-sm text-slate-600 mt-0.5">{n.body}</div>
                  <div className="text-xs text-slate-400 mt-1.5">{(n.created_at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
